import type { RunNoticeLines } from '@/components/setup/RunNotice/run-notice'
import type { SetupRunErrorMessages, SetupRunStageLines } from '@/components/setup/setup-run'
import type {
  PlanGateRefusalReason,
  PlanRunStage,
  PlanRunErrorCode,
  StartPlanRefusalReason,
} from '@/types/plan'

/** The line under a failed plan run for each error code it can end with. */
export const PLAN_RUN_ERROR_MESSAGES = {
  plan_run_enqueue_failed: 'errors.planRun.enqueueFailed',
  plan_run_in_progress: 'errors.planRun.inProgress',
  plan_run_failed: 'errors.planRun.failed',
  brand_domain_unknown: 'errors.planRun.domainUnknown',
  brand_market_undecided: 'errors.planRun.marketUndecided',
  plan_site_unreachable: 'errors.planRun.siteUnreachable',
  plan_raw_store_failed: 'errors.planRun.rawStoreFailed',
  plan_stage_deadline_exceeded: 'errors.planRun.stageDeadlineExceeded',
  vendor_budget_exhausted: 'errors.planRun.vendorBudgetExhausted',
  vendor_not_configured: 'errors.planRun.vendorNotConfigured',
  vendor_http_error: 'errors.planRun.vendorHttpError',
  vendor_envelope_error: 'errors.planRun.vendorEnvelopeError',
  web_content_fetch_failed: 'errors.planRun.contentFetchFailed',
  plan_beliefs_missing: 'errors.planRun.beliefsMissing',
  brand_facts_missing: 'errors.planRun.brandFactsMissing',
  plan_discovery_no_seeds: 'errors.planRun.discoveryNoSeeds',
  model_output_invalid: 'errors.planRun.modelOutputInvalid',
  plan_relevance_nothing_kept: 'errors.planRun.relevanceNothingKept',
  plan_serp_no_results: 'errors.planRun.serpNoResults',
  plan_no_pages_kept: 'errors.planRun.noPagesKept',
  plan_focus_nothing_new: 'errors.planRun.focusNothingNew',
  run_timed_out: 'errors.planRun.timedOut',
} as const satisfies Record<PlanRunErrorCode, string> & SetupRunErrorMessages

/** The line of a plan run for each stage. */
export const PLAN_RUN_STAGE_LINES = {
  inventory: 'strategy.plan.run.stages.inventory',
  content: 'strategy.plan.run.stages.content',
  research: 'strategy.plan.run.stages.research',
  competitors: 'strategy.plan.run.stages.competitors',
  demand: 'strategy.plan.run.stages.demand',
  relevance: 'strategy.plan.run.stages.relevance',
  'topic-candidates': 'strategy.plan.run.stages.topic-candidates',
  'review-topics': 'strategy.plan.run.stages.review-topics',
  fill: 'strategy.plan.run.stages.fill',
  serp: 'strategy.plan.run.stages.serp',
  grouping: 'strategy.plan.run.stages.grouping',
  'type-stage': 'strategy.plan.run.stages.type-stage',
  placement: 'strategy.plan.run.stages.placement',
  'decision-pages': 'strategy.plan.run.stages.decision-pages',
  'site-match': 'strategy.plan.run.stages.site-match',
  'review-plan': 'strategy.plan.run.stages.review-plan',
  commit: 'strategy.plan.run.stages.commit',
} as const satisfies Record<PlanRunStage, string> & SetupRunStageLines

/** The notice lines of a plan run: a tone and line for each status (a failed run names its error instead) and the cost lines. */
export const PLAN_RUN_NOTICE: RunNoticeLines = {
  statuses: {
    running: { tone: 'progress', messageKey: 'strategy.plan.run.working' },
    waiting: { tone: 'info', messageKey: 'strategy.plan.run.waiting' },
    completed: { tone: 'success', messageKey: 'strategy.plan.run.completed' },
    partial: { tone: 'warning', messageKey: 'strategy.plan.run.partial' },
    cancelled: { tone: 'neutral', messageKey: 'strategy.plan.run.cancelled' },
    failed: { tone: 'error', messageKey: 'strategy.plan.run.failed' },
  },
}

/** The toast line for each reason the server refuses a call on a plan run's gate with. */
export const PLAN_GATE_REFUSAL_MESSAGES = {
  plan_gate_missing: 'strategy.plan.proposal.refusals.gateMissing',
  plan_gate_answer_invalid: 'strategy.plan.proposal.refusals.answerInvalid',
  owner_admin_role_required: 'strategy.plan.proposal.refusals.ownerAdminRequired',
} as const satisfies Record<PlanGateRefusalReason, string>

/** The toast line for each reason the server refuses to open a plan run with. */
export const START_PLAN_REFUSAL_MESSAGES = {
  owner_admin_role_required: 'strategy.plan.proposal.refusals.ownerAdminRequired',
  plan_run_in_progress: 'errors.planRun.inProgress',
  plan_focus_needs_plan: 'strategy.findMorePages.needsPlan',
  brand_domain_unknown: 'errors.planRun.domainUnknown',
  brand_market_undecided: 'errors.planRun.marketUndecided',
  vendor_budget_exhausted: 'errors.planRun.vendorBudgetExhausted',
} as const satisfies Record<StartPlanRefusalReason, string>

/** The toast line when the topic a plan call names is gone. */
export const TOPIC_MISSING_MESSAGE = 'strategy.topicPage.missing'
